import React from 'react';

import Error from 'next/error';
import Head from 'next/head';

import _get from 'lodash/get';

import { withCtxMiddleware } from '@bonnet/next/ctx-middleware';
import { withCtxAbortOnStatusCode } from '@bonnet/next/ctx-middleware/with-ctx-abort-on-status-code';
import { withBonnetPage } from '@bonnet/next/page';

import { pageNames } from '@atc/bonnet-paths';
import { useFeatures } from '@atc/features';
import { getScaledImageSource } from '@atc/images';

import { imageSizes } from 'axl-config';

import getScaledPrimaryImg from '@/utilities/getScaledPrimaryImg';
import { saveInvListing } from '@/utilities/saveListing';

import ctxPresetFindCar from '@/context-middleware/ctxPresetFindCar.js';
import addInvDetailsPageData from '@/context-middleware/inv/addInvDetailsPageData';
import dispatchInvDetailsPageData from '@/context-middleware/inv/dispatchInvDetailsPageData';
import withDataIslandDispatcher from '@/context-middleware/withDataIslandDispatcher.js';
import withInterestRates from '@/context-middleware/withInterestRates.js';
import withWalletConfigs from '@/context-middleware/withWalletConfigs.js';

import invActiveEmailListingDuck from '@/ducks/inv/invActiveEmailListingDuck';
import invActiveInteractionDuck from '@/ducks/inv/invActiveInteractionDuck';
import invResultsDuck from '@/ducks/inv/invResultsDuck';
import invSimilarListingsDuck from '@/ducks/inv/invSimilarListingsDuck';

import atcAuthModule from '@/modules/AuthModule.js';
import ParsedQueryModule from '@/modules/ParsedQueryModule.js';

import CommonHeadContainer from '@/containers/CommonHeadContainer';
import HeaderContentContainer from '@/containers/HeaderContentContainer';
import InvAnalytics from '@/containers/inv/InvAnalytics';
import InvDetailsContainer from '@/containers/inv/InvDetailsContainer';
import InvEmailModalContainer from '@/containers/inv/InvEmailModalContainer';
import InvPageEventsContainer from '@/containers/inv/InvPageEventsContainer';
import InvSimilarListingsContainer from '@/containers/inv/InvSimilarListingsContainer';
import MyWalletContainer from '@/containers/MyWalletContainer';

const invDucks = {
    invActiveEmailListingDuck,
    invActiveInteractionDuck,
    invResultsDuck,
    invSimilarListingsDuck,
};

const { INVENTORY_DETAILS } = pageNames;

function ExpandedHybridVdpPage({
    browser,
    currentUrl,
    images,
    statusCode,
    query,
}) {

    const {
        my_wallet: [enableMyWallet],
    } = useFeatures([
        'my_wallet',
    ]);

    const primary = _get(images, 'primary', 0);
    const primaryImgSrc = _get(images, `sources[${primary}].src`, '');
    const scaledPrimaryImage = primaryImgSrc && getScaledPrimaryImg(INVENTORY_DETAILS, browser, primaryImgSrc);
    const openGraphImage = primaryImgSrc && getScaledImageSource({
        src: primaryImgSrc,
        ...imageSizes.openGraphImg,
    });

    if (statusCode) {
        return <Error statusCode={statusCode} />;
    }

    return (
        <div className="container">
            <HeaderContentContainer saveListing={saveInvListing} />
            <CommonHeadContainer
                currentUrl={currentUrl}
                openGraphImage={openGraphImage}
                pageName={INVENTORY_DETAILS}
                query={query}
            />
            <Head>
                <link
                    rel="preload"
                    as="image"
                    href={scaledPrimaryImage}
                />
            </Head>

            <main aria-labelledby="inventory-details-heading">
                <InvDetailsContainer />
                <hr />
                <InvSimilarListingsContainer />
            </main>

            {enableMyWallet && <MyWalletContainer />}

            <InvEmailModalContainer />

            <InvPageEventsContainer />
            <InvAnalytics />
        </div>
    );
}

ExpandedHybridVdpPage.getInitialProps = async (ctx) => {

    await withCtxMiddleware([
        ctxPresetFindCar({
            pageName: INVENTORY_DETAILS,
            fetcher: addInvDetailsPageData,
            dispatcher: dispatchInvDetailsPageData,
            preFetchCtxMiddleware: [
                ctx.withModuleCtxMiddleware({
                    batchDispatch: false,
                    fetchCompositeJSON: false,
                }),
            ],
        }),
        withCtxAbortOnStatusCode(),
        withInterestRates(),
        withWalletConfigs(),
        withDataIslandDispatcher(),
    ], ctx);

    // if the current ctx contains a status code then we need to abort the process and return it
    // to allow the page to render an error
    if (ctx.statusCode) {
        return {
            statusCode: ctx.statusCode,
        };
    }
    return {
        currentUrl: ctx.data.currentUrl,
        images: _get(ctx, 'data.listings[0].images'),
        query: ctx.query,
        dataIsland: ctx.data.dataIsland,
        browser: ctx.store.getState().browser,
    };
};

const { BonnetPageComponent } = withBonnetPage(ExpandedHybridVdpPage, {
    reducers: [
        ...Object.values(invDucks),
    ],
    modules: [
        atcAuthModule,
        ParsedQueryModule,
    ],
});

export default BonnetPageComponent;
